import PropTypes from 'prop-types';
import {
    Avatar,
    Box,
    Divider,
    Grid,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Wc';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { genderOptions } from '../Users/config/select';

const InfoItem = ({ icon: Icon, label, value }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Box display="flex" alignItems="center" mb={1}>
            <Icon sx={{ color: '#1976d2', mr: 1 }} />
            <Box>
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                    {value || '—'}
                </Typography>
            </Box>
        </Box>
    </Grid>
);

const PersonalData = ({ user, expanded, onToggle }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        name,
        email,
        phone,
        birth_date,
        sex,
        address = {},
        health_id,
        health_id_type,
        last_access,
        gender,
        user_data,
        role_uuid
    } = user;

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [openQrDialog, setOpenQrDialog] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [qrKey, setQrKey] = useState(Date.now());

    const playStoreUrl = `https://md.connected-heart.eu/login?uuid=${role_uuid}`;

    useEffect(() => {
        if (!openQrDialog) return;
        setCountdown(60);
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [openQrDialog, qrKey]);

    const fullAddress = [address?.street, address?.city, address?.zip]
        .filter(Boolean)
        .join(', ');

    const getSexLabel = (value) => {
        const map = { FEMALE: 0, MALE: 1, OTHER: 2 };
        const index = map[value];
        return t(`sexOptions.${index}.name`, value);
    };

    const getGenderLabel = (value) => {
        const map = {
            WOMAN: 0,
            MAN: 1,
            TRANS_WOMAN: 2,
            TRANS_MAN: 3,
            NON_BINARY: 4,
            AGENDER: 5,
            GENDERQUEER: 6,
            PREFER_NOT_TO_SAY: 7,
            NOT_LISTED: 8
        };
        const index = map[value];
        return genderOptions[index]?.name || value;
    };

    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleEdit = () => {
        navigate('/users/user/edit', { state: { user: { role_uuid } } });
        handleMenuClose();
    };

    const handleAddStressTest = () => {
        handleMenuClose();
        navigate('/users/user/stress-test/create', {
            state: { patient: user }
        });
    };

    const handleOpenMessages = () => {
        handleMenuClose();
        navigate(`/mensagens/${user.role_uuid}`);
    };

    {
        /** const handleOpenQr = () => {
        setOpenQrDialog(true);
        handleMenuClose();
    };


    const handleRegenerateQr = () => {
        setQrKey(Date.now());
        setCountdown(60);
    };**/
    }

    return (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            {/* Header */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={expanded ? 3 : 0}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                        sx={{
                            width: 80,
                            height: 80,
                            bgcolor: '#1976d2',
                            fontSize: 36
                        }}
                    >
                        {name?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="h2" fontWeight="bold">
                        {name || t('userPage.defaultName')}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={handleMenuOpen}>
                        <SettingsIcon />
                    </IconButton>
                    <IconButton onClick={onToggle}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Dropdown Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEdit}>{t('userPage.editPatientData')}</MenuItem>
                <MenuItem onClick={handleAddStressTest}>
                    {t('userPage.addStressTest')}
                </MenuItem>
                <MenuItem onClick={handleOpenMessages}>
                    {t('userPage.openMessages')}
                </MenuItem>
                {/**<MenuItem onClick={handleOpenQr}>
                    Gerar código QR para início de sessão
                </MenuItem>**/}
            </Menu>

            {/* QR Code Dialog */}
            {/**
            <Dialog open={openQrDialog} onClose={() => setOpenQrDialog(false)}>
                <DialogTitle
                    sx={{
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#1976d2'
                    }}
                >
                    Digitalize o QR Code para iniciar sessão
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <img
                        key={qrKey}
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                            playStoreUrl
                        )}`}
                        alt="QR Code"
                    />
                    <Typography sx={{ mt: 2 }}>
                        Código expira em: {countdown} segundos
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            color: '#1976d2',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}
                        onClick={handleRegenerateQr}
                    >
                        Gerar código de início de sessão novamente
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button
                        onClick={() => setOpenQrDialog(false)}
                        sx={{
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            borderRadius: '8px',
                            px: 4,
                            py: 1.2,
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: '#115293'
                            }
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
            **/}

            <Collapse in={expanded}>
                <Divider sx={{ mb: 3 }} />

                {/* Contacto e Identificação */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" gutterBottom>
                    {t('userPage.contactAndIdentification')}
                </Typography>
                <Grid container spacing={2} mb={3}>
                    <InfoItem
                        icon={EmailIcon}
                        label={t('userPage.email')}
                        value={email}
                    />
                    <InfoItem
                        icon={PhoneIcon}
                        label={t('userPage.phone')}
                        value={phone}
                    />
                    <InfoItem
                        icon={HomeIcon}
                        label={t('userPage.address')}
                        value={fullAddress}
                    />
                    <InfoItem
                        icon={CalendarTodayIcon}
                        label={t('userPage.birthDate')}
                        value={birth_date ? dayjs(birth_date).format('DD/MM/YYYY') : null}
                    />
                    <InfoItem
                        icon={BadgeIcon}
                        label={t('userPage.sex')}
                        value={getSexLabel(sex)}
                    />
                    <InfoItem
                        icon={PersonIcon}
                        label={t('userPage.gender')}
                        value={getGenderLabel(gender)}
                    />
                </Grid>

                {/* Dados Administrativos */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" gutterBottom>
                    {t('userPage.adminData')}
                </Typography>
                <Grid container spacing={2} mb={3}>
                    <InfoItem
                        icon={HealthAndSafetyIcon}
                        label={t('userPage.healthNumber')}
                        value={health_id}
                    />
                    <InfoItem
                        icon={HealthAndSafetyIcon}
                        label={t('userPage.healthNumberType')}
                        value={health_id_type}
                    />
                    <InfoItem
                        icon={AccessTimeIcon}
                        label={t('userPage.lastAccess')}
                        value={
                            last_access
                                ? dayjs(last_access).format('DD/MM/YYYY HH:mm')
                                : null
                        }
                    />
                </Grid>

                {/* Dados de Saúde */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" gutterBottom>
                    {t('userPage.healthData')}
                </Typography>
                <Grid container spacing={2}>
                    <InfoItem
                        icon={FavoriteIcon}
                        label={t('userPage.maxHR')}
                        value={user_data?.maxHeartRate}
                    />
                    <InfoItem
                        icon={FavoriteBorderIcon}
                        label={t('userPage.restingHR')}
                        value={user_data?.restingHeartRate}
                    />
                    <InfoItem
                        icon={FitnessCenterIcon}
                        label={t('userPage.trainingHR')}
                        value={user_data?.trainingHeartRate}
                    />
                </Grid>
            </Collapse>
        </Paper>
    );
};

PersonalData.propTypes = {
    user: PropTypes.object,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired
};

export default PersonalData;
